import React, { useState } from 'react';
import { View, ViewStyle, StyleProp, TextStyle } from 'react-native';
import MyAppText from './MyAppText';
import { TextInputMask } from 'react-native-masked-text';
import { translate } from '../services/translate';
import { useStyles } from 'react-native-unistyles';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';
import inputStylesheet from '../styles/inputStyles';

export type PhoneCode = string;

type MyPhoneInputProps = {
    label: string;
    placeholder?: string;
    viewStyle?: StyleProp<ViewStyle>;
    labelStyle?: StyleProp<TextStyle>;
    required?: boolean;
    disabled?: boolean;
} & ({
    value: string;
    invalid: (value: string) => boolean;
    onChangeText: (value: string) => void;
    form?: never;
    fieldName?: never;
    customValidations?: never;
    pattern?: never;
} | {
    value?: never;
    invalid?: never;
    onChangeText?: never;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
    pattern?: {
        value: RegExp;
        message: string;
    };
});

export default function MyPhoneInput({ label, placeholder, value, invalid, onChangeText, viewStyle, labelStyle, required, form, fieldName, customValidations, pattern, disabled }: MyPhoneInputProps) {
    const [isInvalid, setIsInvalid] = useState(false);
    const { styles: defaultInputStyle } = useStyles(inputStylesheet);

    if (form && fieldName) {
        const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};
        if (required) {
            rules.required = translate('required_field');
        }

        if (pattern) {
            rules.pattern = pattern;
        }

        if (customValidations) {
            rules.validate = { ...customValidations };
        }

        return <Controller
            control={form.control}
            name={fieldName}
            rules={rules}
            render={({ field, fieldState }) => (
                <View style={[defaultInputStyle.container, viewStyle]}>
                    <View style={defaultInputStyle.labelContainer}>
                        {label && <MyAppText style={labelStyle ? labelStyle : defaultInputStyle.label}>{label}</MyAppText>}
                        {required && label ?
                            <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                            : null}
                    </View>
                    <TextInputMask
                        placeholder={placeholder ?? '(00) 0 0000-0000'}
                        onBlur={field.onBlur}
                        value={field.value}
                        includeRawValueInChangeText={true}
                        onChangeText={(maskedText, rawText) => {
                            field.onChange(rawText || '');
                        }}
                        type={'cel-phone'}
                        style={defaultInputStyle.input({
                            invalid: Boolean(fieldState.error),
                            disabled,
                            placeholder: !field.value,
                        })}
                        options={{
                            maskType: 'BRL',
                            withDDD: true,
                            dddMask: '(99) '
                        }}
                        editable={!disabled}
                    />
                    {isInvalid
                        ? <MyAppText style={defaultInputStyle.errorMessage}>{translate('invalidPhone')}</MyAppText>
                        : fieldState.error ? <MyAppText style={defaultInputStyle.errorMessage}>{fieldState.error.message}</MyAppText> : null}
                </View>
            )}
        />;
    } else if (value !== undefined && onChangeText !== undefined) {
        return (
            <View style={[defaultInputStyle.container, viewStyle]}>
                <View style={defaultInputStyle.labelContainer}>
                    <MyAppText style={defaultInputStyle.label}>{label}</MyAppText>
                    {required ?
                        <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                        : null}
                </View>
                <TextInputMask
                    placeholder={placeholder ?? '(00) 0 0000-0000'}
                    value={value}
                    includeRawValueInChangeText={true}
                    onChangeText={(maskedText, rawText) => {
                        onChangeText(rawText || '');
                    }}
                    type={'cel-phone'}
                    style={defaultInputStyle.input({
                        invalid: Boolean(isInvalid),
                        disabled,
                        placeholder: !value,
                    })}
                    options={{
                        maskType: 'BRL',
                        withDDD: true,
                        dddMask: '(99) '
                    }}
                    onBlur={() => {
                        setIsInvalid(invalid(value));
                    }}
                    editable={!disabled}
                />
                {isInvalid ?
                    <MyAppText style={defaultInputStyle.errorMessage}>{translate('invalidPhone')}</MyAppText> : null}
            </View>
        );
    }
}
